import { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, Linkedin } from 'lucide-react';

const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

const verifiedLinkedInUrl = value => {
  if (!value) return '';
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && (url.hostname === 'linkedin.com' || url.hostname.endsWith('.linkedin.com')) && /^\/in\/.+/.test(url.pathname) ? url.href : '';
  } catch { return ''; }
};

export const TeamProfileModal = ({ member, onClose, onPrev, onNext, position }) => {
  const panelRef = useRef(null);
  const touchStart = useRef(null);

  const keyHandler = useCallback(event => {
    if (event.key === 'Escape') { onClose(); return; }
    if (event.key === 'ArrowLeft') { onPrev(); return; }
    if (event.key === 'ArrowRight') { onNext(); return; }
    if (event.key !== 'Tab') return;
    const nodes = [...(panelRef.current?.querySelectorAll(FOCUSABLE) || [])];
    if (!nodes.length) return;
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }, [onClose, onPrev, onNext]);

  useEffect(() => {
    document.addEventListener('keydown', keyHandler);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.__techspitzeLenis?.stop();
    const timer = setTimeout(() => panelRef.current?.querySelector(FOCUSABLE)?.focus(), 60);
    return () => {
      document.removeEventListener('keydown', keyHandler);
      document.body.style.overflow = previousOverflow;
      window.__techspitzeLenis?.start();
      clearTimeout(timer);
    };
  }, [keyHandler]);

  const linkedinUrl = verifiedLinkedInUrl(member?.linkedinUrl);

  return createPortal(<AnimatePresence>
    {member && <motion.div className="team-modal-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.22 }} onClick={onClose} data-testid="team-modal-backdrop">
      <motion.div ref={panelRef} className="team-modal-panel" role="dialog" aria-modal="true" aria-labelledby="team-modal-name"
        initial={{ opacity: 0, scale: 0.96, y: 24 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.97, y: 18 }}
        transition={{ duration: 0.26, ease: [0.22, 1, 0.36, 1] }} onClick={e => e.stopPropagation()}
        onTouchStart={e => { touchStart.current = e.touches[0].clientY; }}
        onTouchEnd={e => { if (touchStart.current !== null && e.changedTouches[0].clientY - touchStart.current > 90) onClose(); touchStart.current = null; }}
        data-testid="team-modal">
        <span className="team-modal-grabber" aria-hidden="true" />
        <button type="button" className="team-modal-close" onClick={onClose} aria-label="Close profile" data-testid="team-modal-close"><X size={18} /></button>

        <div className="team-modal-photo" data-testid="team-modal-photo">
          {member.image ? <img src={member.image} alt={member.name} loading="lazy" onError={e => { e.currentTarget.style.display = 'none'; }} />
            : <span className="team-modal-photo-fallback">Photo coming soon</span>}
        </div>

        <div className="team-modal-body" data-lenis-prevent>
          <span className="team-modal-role" data-testid="team-modal-role">{member.title || 'TechSpitze Team'}</span>
          <h2 id="team-modal-name" data-testid="team-modal-name">{member.name}</h2>
          <p data-testid="team-modal-bio">{member.bio || 'A fuller profile for this team member is on the way.'}</p>
          {linkedinUrl && <a className="team-modal-linkedin" href={linkedinUrl} target="_blank" rel="noopener noreferrer"
            aria-label={`View ${member.name}’s LinkedIn profile (opens in a new tab)`} data-testid="team-modal-linkedin">
            <Linkedin size={16} aria-hidden="true" /><span>LinkedIn</span>
          </a>}
        </div>

        <div className="team-modal-nav">
          <button type="button" onClick={onPrev} aria-label="Previous team member" data-testid="team-modal-prev"><ChevronLeft size={18} /></button>
          <span data-testid="team-modal-position">{position}</span>
          <button type="button" onClick={onNext} aria-label="Next team member" data-testid="team-modal-next"><ChevronRight size={18} /></button>
        </div>
      </motion.div>
    </motion.div>}
  </AnimatePresence>, document.body);
};
